import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Package, User } from "lucide-react";
import { adminLogin, customerRegister, formatDkk, forgotPassword, getMe, getMyOrders, googleSession, updateMe } from "@/lib/site";

const field =
  "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-cream placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-forest";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"
    />
    <path
      fill="#FBBC05"
      d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z"
    />
    <path
      fill="#EA4335"
      d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"
    />
  </svg>
);

const startGoogleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/min-konto";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

function GoogleCallback({ onDone }) {
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = window.location.hash.split("session_id=")[1]?.split("&")[0];
    window.history.replaceState(null, "", window.location.pathname);
    if (!sessionId) {
      onDone(null);
      return;
    }
    googleSession(sessionId)
      .then((res) => {
        localStorage.setItem("gf_customer", res.token);
        toast.success("Velkommen! Du er logget ind med Google.");
        onDone(res.token);
      })
      .catch(() => {
        toast.error("Google-login mislykkedes. Prøv igen.");
        onDone(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div
      data-testid="google-callback-processing"
      className="flex min-h-[80vh] items-center justify-center pt-32"
    >
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-6 w-6 animate-spin text-forest" />
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-sage">
          Logger ind med Google …
        </p>
      </div>
    </div>
  );
}

export default function MinKontoPage() {
  const location = useLocation();
  const [token, setToken] = useState(localStorage.getItem("gf_customer") || "");
  const [googleHandled, setGoogleHandled] = useState(false);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const loadAccount = async (t) => {
    try {
      const [me, myOrders] = await Promise.all([getMe(t), getMyOrders(t)]);
      setProfile(me);
      setOrders(myOrders);
      localStorage.setItem("gf_customer_user", JSON.stringify(me));
    } catch {
      localStorage.removeItem("gf_customer");
      localStorage.removeItem("gf_customer_user");
      setToken("");
    }
  };

  useEffect(() => {
    if (token) loadAccount(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        const res = await forgotPassword({
          email: form.email,
          origin_url: window.location.origin,
        });
        toast.success(res.message);
        setMode("login");
        return;
      }
      const res =
        mode === "login"
          ? await adminLogin(form.email, form.password)
          : await customerRegister(form);
      localStorage.setItem("gf_customer", res.token);
      setToken(res.token);
      toast.success(mode === "login" ? "Velkommen tilbage!" : "Din konto er oprettet!");
      await loadAccount(res.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Noget gik galt. Prøv igen.");
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await updateMe({
        name: profile.name,
        phone: profile.phone,
        address: profile.address,
        postal_code: profile.postal_code,
      });
      setProfile(updated);
      localStorage.setItem("gf_customer_user", JSON.stringify(updated));
      toast.success("Dine oplysninger er gemt");
    } catch {
      toast.error("Kunne ikke gemme. Prøv igen.");
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("gf_customer");
    localStorage.removeItem("gf_customer_user");
    setToken("");
    setProfile(null);
  };

  // Synkron tjek af URL-fragment (useLocation().hash) FØR login-UI vises — forhindrer race conditions
  if (location.hash?.includes("session_id=") && !googleHandled) {
    return (
      <GoogleCallback
        onDone={(newToken) => {
          setGoogleHandled(true);
          if (newToken) {
            setToken(newToken);
            loadAccount(newToken);
          }
        }}
      />
    );
  }

  if (!token) {
    return (
      <div className="flex min-h-[80vh] items-center justify-center px-6 pb-24 pt-36">
        <div className="w-full max-w-md border border-white/10 bg-moss p-8">
          <div className="mb-8 flex items-center gap-3">
            <User className="h-5 w-5 text-forest" />
            <h1 className="font-serif text-3xl tracking-tight text-cream">
              {mode === "login" ? "Log ind" : mode === "register" ? "Opret konto" : "Glemt adgangskode"}
            </h1>
          </div>
          {mode !== "forgot" ? (
            <div className="mb-6 flex gap-2">
              {[
                ["login", "Log ind"],
                ["register", "Opret konto"],
              ].map(([m, label]) => (
                <button
                  key={m}
                  data-testid={`account-mode-${m}`}
                  onClick={() => setMode(m)}
                  className={`rounded-full border px-5 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                    mode === m
                      ? "border-forest bg-forest text-cream"
                      : "border-white/15 text-sage hover:border-white/40 hover:text-cream"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : (
            <p className="mb-6 text-sm text-sage">
              Indtast din e-mail, så sender vi et link til at vælge en ny adgangskode.
            </p>
          )}
          <form data-testid="account-form" onSubmit={submit} className="space-y-5">
            {mode === "register" && (
              <>
                <input
                  data-testid="account-name-input"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Fulde navn"
                  className={field}
                />
                <input
                  data-testid="account-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Telefon (valgfri)"
                  className={field}
                />
              </>
            )}
            <input
              data-testid="account-email-input"
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="E-mail"
              className={field}
            />
            {mode !== "forgot" && (
              <input
                data-testid="account-password-input"
                type="password"
                value={form.password}
                onChange={set("password")}
                placeholder="Adgangskode (min. 6 tegn)"
                className={field}
              />
            )}
            <button
              data-testid="account-submit-button"
              type="submit"
              disabled={busy}
              className="flex w-full items-center justify-center gap-3 rounded-full bg-forest py-4 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-pale hover:text-ink disabled:opacity-60"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : mode === "login" ? (
                "Log ind"
              ) : mode === "register" ? (
                "Opret konto"
              ) : (
                "Send nulstillingslink"
              )}
            </button>
          </form>
          {mode !== "forgot" && (
            <>
              <div className="my-6 flex items-center gap-4">
                <span className="h-px flex-1 bg-white/10" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-sage">
                  eller
                </span>
                <span className="h-px flex-1 bg-white/10" />
              </div>
              <button
                data-testid="google-login-button"
                type="button"
                onClick={startGoogleLogin}
                className="flex w-full items-center justify-center gap-3 rounded-full border border-white/15 py-4 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:border-white/40 hover:bg-white/5"
              >
                <GoogleIcon /> Fortsæt med Google
              </button>
            </>
          )}
          {mode === "forgot" ? (
            <button
              data-testid="account-back-to-login"
              onClick={() => setMode("login")}
              className="mt-6 w-full text-center font-mono text-[11px] uppercase tracking-[0.15em] text-sage transition-colors hover:text-cream"
            >
              Tilbage til log ind
            </button>
          ) : (
            <button
              data-testid="account-forgot-link"
              onClick={() => setMode("forgot")}
              className="mt-6 w-full text-center font-mono text-[11px] uppercase tracking-[0.15em] text-sage transition-colors hover:text-cream"
            >
              Glemt adgangskode?
            </button>
          )}
          <p className="mt-6 text-center text-xs text-sage">
            Gem dine oplysninger og se dine ordrer ét sted.
          </p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center pt-32">
        <Loader2 className="h-6 w-6 animate-spin text-forest" />
      </div>
    );
  }

  return (
    <div data-testid="account-page" className="mx-auto max-w-5xl px-6 pb-24 pt-36 lg:px-16">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl tracking-tight text-cream">Min konto</h1>
          <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-sage">
            {profile.email}
          </p>
        </div>
        <button
          data-testid="account-logout-button"
          onClick={logout}
          className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-sage transition-colors hover:text-cream"
        >
          <LogOut className="h-4 w-4" /> Log ud
        </button>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-2">
        <form
          onSubmit={saveProfile}
          className="space-y-5 border border-white/10 bg-moss p-8"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-pale">
            Mine oplysninger
          </p>
          <input
            data-testid="profile-name-input"
            value={profile.name || ""}
            onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
            placeholder="Fulde navn"
            className={field}
          />
          <input
            data-testid="profile-phone-input"
            value={profile.phone || ""}
            onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
            placeholder="Telefon"
            className={field}
          />
          <input
            data-testid="profile-address-input"
            value={profile.address || ""}
            onChange={(e) => setProfile((p) => ({ ...p, address: e.target.value }))}
            placeholder="Adresse"
            className={field}
          />
          <input
            data-testid="profile-postal-input"
            value={profile.postal_code || ""}
            onChange={(e) => setProfile((p) => ({ ...p, postal_code: e.target.value }))}
            placeholder="Postnummer"
            maxLength={4}
            className={field}
          />
          <button
            data-testid="save-profile-button"
            type="submit"
            disabled={saving}
            className="flex items-center gap-3 rounded-full bg-forest px-7 py-3 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pale hover:text-ink disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gem oplysninger"}
          </button>
        </form>

        <div className="border border-white/10 bg-moss p-8">
          <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-pale">
            <Package className="h-4 w-4 text-forest" />
            Mine ordrer
          </p>
          <div data-testid="my-orders" className="mt-6">
            {orders.length === 0 && (
              <p className="text-sm text-sage">Du har ingen ordrer endnu.</p>
            )}
            {orders.map((o) => (
              <div key={o.session_id} className="border-b border-white/10 py-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-sage">
                    {(o.created_at || "").slice(0, 10)}
                  </span>
                  <span
                    className={`font-mono text-[10px] uppercase tracking-[0.2em] ${
                      o.payment_status === "paid" ? "text-pale" : "text-sage"
                    }`}
                  >
                    {o.payment_status === "paid" ? "Betalt" : o.status}
                  </span>
                </div>
                <p className="mt-2 text-cream">
                  {(o.items || []).map((i) => `${i.qty}× ${i.name}`).join(", ")}
                </p>
                <p className="mt-1 font-mono text-xs text-pale">
                  {formatDkk(o.amount_dkk)}
                  <span className="ml-3 text-sage">
                    {o.shipping === "afhentning"
                      ? `Afhentning · ${o.pickup_address || "Fælleden 16, 7190 Billund"}`
                      : o.shipping === "levering"
                        ? "Personlig levering"
                        : "Forsendelse"}
                  </span>
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
